import {
  type ChangePasswordDto,
  type MeResponse,
  type UpdateAvatarDto,
  type UpdateProfileDto,
  meApi,
} from '@/api/me';
import { useAuthStore } from '@/stores/auth';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export function useMe() {
  const access = useAuthStore((s) => s.access);
  return useQuery({
    queryKey: ['me'],
    queryFn: () => meApi.getMe(),
    enabled: !!access,
    staleTime: 5 * 60 * 1000,
  });
}

export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: UpdateProfileDto) => meApi.updateProfile(dto),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me'] }),
  });
}

function syncAvatar(queryClient: ReturnType<typeof useQueryClient>, avatarUrl: string | null) {
  queryClient.setQueryData<MeResponse>(['me'], (current) =>
    current ? { ...current, user: { ...current.user, avatar_url: avatarUrl } } : current,
  );
  const user = useAuthStore.getState().user;
  if (user) useAuthStore.getState().setUser({ ...user, avatar_url: avatarUrl });
}

export function useUpdateAvatar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: UpdateAvatarDto) => meApi.updateAvatar(dto),
    onSuccess: (data) => syncAvatar(qc, data.avatar_url),
  });
}

export function useRemoveAvatar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => meApi.removeAvatar(),
    onSuccess: () => syncAvatar(qc, null),
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (dto: ChangePasswordDto) => meApi.changePassword(dto),
  });
}
